'use client';

import { useContext, useEffect, useState, useRef } from 'react';
import Box from '@mui/material/Box';
import DataContext from '@/contexts/DataContext';
import { fetchExprData, fetchTooltip, extractTrackedParams, extractChangedParams } from '@/lib/dataHandlers/DataFetchers';
import { useComponentLogger } from '@/lib/component-logger';
import { getDefaultColor } from '@/lib/dataHandlers/ChartHelpers';
import { InfoTooltip } from '../typography/info-tooltip';
import { textColors, chartColors } from '@/styles/theme/colors';
import Typography from '@mui/material/Typography';
import {  RadialBar, RadialBarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { NoSsr } from '@/components/core/no-ssr';
export function Gauge ({config, isLastObject=false}) {
    const logger = useComponentLogger("Gauge");
    // logger.debug('gauge object', gaugeObject, isLastObject);
    const chartHeight = 300;
    const { currentProject, selectedViewParams, changedParams } = useContext(DataContext);
    const [gaugeConfig, setGaugeConfig] = useState({});
    const [gaugeTopMargin, setGaugeTopMargin] = useState('20px');
    // note: the gauge needs the first bar to be 100, which we hide
    // for some reason this allows the true value of the gauge to render, in the 2nd bar
    // this trick comes from the devias template
    // if you remove the 1st bar, the gauge will always be 100% full for any val > 0
    const [gaugeData, setGaugeData] = useState([{name: "empty",value: 100}]);
    const [tooltip, setTooltip] = useState(null);
    const [title, setTitle] = useState(config.title || '');
    const [textColor, setTextColor] = useState(null);
    const [gaugeColor, setGaugeColor] = useState("blue");
    const trackedParamValsRef = useRef({});
    const trackedParamNamesRef = useRef({}); // names of the params that this component depends on, extracted from its api expr


    // fetch and set content
    const fetchContent = async () => {
        if (config.dataSource === null || config.dataSource === undefined) return;

        const dataSource = config.dataSource;
        
        if (dataSource.type === "text") {
            setImgURL(config.dataSource.text);
            logger.debug('gauge content', config.dataSource.text);
        } else if (dataSource.type === "apiExpr") {
            const data = await fetchExprData(currentProject, selectedViewParams, dataSource.expr);
            logger.debug('gauge data', data.rows);

            if (data?.rows && data.rows.length > 0) {
                let apiConfig = data.rows[0];
                setGaugeConfig(apiConfig);

                // lower the value if there's no subtext
                if (apiConfig.subText) {
                    setGaugeTopMargin('20px');
                } else setGaugeTopMargin('40px');

                let chartDataArr = [];
                chartDataArr.push({
                    name: "empty",
                    value: 100
                })
                let chartDataObj = {
                    name: apiConfig.name || 'gauge', 
                    value: 0, 
                    // fill: chartColors['blue']
                }


                if (apiConfig.title) setTitle(apiConfig.title);
                
                if (apiConfig.textColor) {
                    let color = textColors[apiConfig.textColor];
                    if (color) setTextColor(color);
                    if (color && !apiConfig.gaugeColor) setGaugeColor(color);
                }

                if (apiConfig.gaugeColor) {
                    let color = chartColors[apiConfig.gaugeColor];
                    if (color) setGaugeColor(color);
                    if (color && !apiConfig.textColor) setTextColor(color);
                } else {
                    let colorData = getDefaultColor(0, []);
                    let color = colorData.color;
                    // logger.debug('gauge color', color);
                    if (color) setGaugeColor(color);
                }

                if (apiConfig.val !== null && apiConfig.val !== undefined && apiConfig.min !== null && apiConfig.min !== undefined && apiConfig.max !== null && apiConfig.max !== undefined) {
                    let range = apiConfig.max - apiConfig.min;
                    let scaledVal = (apiConfig.val.val / range) * 100;

                    chartDataObj.value = scaledVal;
                    chartDataArr.push(chartDataObj);
                    setGaugeData(chartDataArr);

                    // logger.debug('gauge val', scaledVal, config.val.val, config.min, config.max, chartDataObj);
                }
            }
        }

    }

    const processTooltip = async() => {
        let tooltipContent = await fetchTooltip(currentProject, selectedViewParams, config);
        // logger.debug('gauge tooltip', tooltipContent);
        if (tooltipContent) setTooltip(tooltipContent);
    }

    useEffect(() => {
        logger.debug('gauge config', config)
        let trackedParams = extractTrackedParams(config.dataSource);
        let params = {};
        for (const param of trackedParams) {
            if (selectedViewParams[param]) params[param] = selectedViewParams[param].value;
        }

        logger.debug('tracked params', params);
        trackedParamValsRef.current = params;
        trackedParamNamesRef.current = trackedParams;
        fetchContent().catch(() => {});
        processTooltip().catch(() => {});
    }, []);

    useEffect(() => {
        let shouldReFetch = extractChangedParams(trackedParamNamesRef.current, changedParams, selectedViewParams, trackedParamValsRef.current);
        logger.debug('changed params', changedParams, trackedParamNamesRef.current, shouldReFetch);
        if (shouldReFetch.changed) {
            trackedParamValsRef.current = shouldReFetch.paramRef;
            fetchContent();
            processTooltip();
        }

        // make sure to clear the content if any of the tracked params were removed
        // common use case: when a parent param changes, it clears the child param
        for (const param of trackedParamNamesRef.current) { 
            if (!selectedViewParams[param]) {
                setGaugeData([{name: "empty",value: 100}]);
                return;
            }
        }
    }, [changedParams]);

    return (
        <Box 
            direction={'column'} 
            sx={{
                alignItems: 'center',
                paddingBottom: isLastObject ? '2rem' : null,
            }}
        >
        {/* <Stack direction={'column'} sx={{alignItems: 'flex-start'}}> */}
            <Typography color="text.secondary" variant="h6" sx={{display: 'flex', alignItems: 'center'}}>
                {title}
                {tooltip ? <InfoTooltip content={tooltip} /> : <></>}
            </Typography>
            {/* <NoSsr fallback={<Box sx={{ height: `${chartHeight}px` }} />}> */}
            <NoSsr fallback={<Box sx={{ height: `${chartHeight}px`}} />}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        position: 'relative',
                        // hide the empty bar
                        '& .recharts-layer path[name="empty"]': { display: 'none' },
                        '& .recharts-layer .recharts-radial-bar-background-sector': { fill: 'var(--mui-palette-neutral-100)' },
                        // '& .recharts-responsive-container': {paddingBottom: '-100%', marginBottom: '-100%'},
                        // '& .recharts-surface': {paddingBottom: '-100%', marginBottom: '-100%'}
                    }}
                >
                    
                    {/* <RadialBarChart
                        barSize={24}
                        gaugeObject={gaugeObject}
                        endAngle={-10}
                        height={chartHeight}
                        innerRadius={166}
                        startAngle={190}
                        width={chartHeight}
                    > */}
                    <ResponsiveContainer width={chartHeight} aspect={1}>
                    <RadialBarChart
                        barSize={24}
                        data={gaugeData}
                        endAngle={-10}
                        height={chartHeight}
                        aspect={1}
                        width={chartHeight}
                        innerRadius={166}
                        startAngle={190}
                        // for some reason recharts adds a large whitespace at bottom of gauge
                        style={{marginBottom: '-40%'}} 
                    >
                        <RadialBar
                            animationDuration={300}
                            background
                            cornerRadius={10}
                            dataKey="value"
                            endAngle={-320}
                            fill={gaugeColor}
                            startAngle={20}
                        />
                    </RadialBarChart>
                    </ResponsiveContainer>
                        {/* Title */}
                        <Box
                        sx={{
                            alignItems: 'center',
                            bottom: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            left: 0,
                            position: 'absolute',
                            right: 0,
                            top: 0,
                        }}
                    >
                    {/* Value */}
                    {gaugeConfig?.val ? 
                        // <Box sx={{ textAlign: 'center', mt: '-80px' }}>
                        <Box sx={{ textAlign: 'center', mt: gaugeTopMargin}}>
                        <Typography color={textColor} variant="h4">
                            {/* {gaugeConfig.val.val + gaugeConfig.val.unit || ''} */}
                            {gaugeConfig.val.val % 1 > 0 ? 
                                `${gaugeConfig.val.val.toFixed(1)}${gaugeConfig.val.unit}` 
                                : `${gaugeConfig.val.val}${gaugeConfig.val.unit}`
                            }
                        </Typography>
                        </Box>
                    : <></>}
                        
                    </Box>
                    {/* Subtitle */}
                    {gaugeConfig?.subText ? 
                        <Box
                        sx={{
                            alignItems: 'center',
                            bottom: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            left: 0,
                            position: 'absolute',
                            right: 0,
                            top: 0,
                        }}
                    >
                        {/* <Box sx={{ textAlign: 'center', mt: '-10px' }}> */}
                        <Box sx={{ textAlign: 'center', mt: '100px' }}>
                            <Typography color={"text.secondary"} variant="h5">
                                {/* {new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 2 }).format(usage / 100)} */}
                                {gaugeConfig.subText}
                            </Typography>
                        </Box>
                    </Box>
                    : <></>}
                    
                </Box>
            </NoSsr>                
        </Box>
    );
}